import { useEffect, useState } from "react";
import { api } from "@/lib/api";
import DashboardShell from "@/components/layout/DashboardShell";
import { toast } from "sonner";
import { useT } from "@/lib/i18n";
import { TEST } from "@/constants/testIds";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import LanguageSelect from "@/components/LanguageSelect";
import DeviceSyncPanel from "@/components/DeviceSyncPanel";
import DataManagementCard from "@/components/DataManagementCard";
import { Languages, BadgeIndianRupee } from "lucide-react";
import HelpVideoButton from "@/components/HelpVideoButton";

const REG_PRICE_TYPES = ["cow", "buffalo", "goat", "sheep", "horse"];

/** Owner-only: ₹/animal registration price per animal type (per haat). */
function RegistrationPricingCard() {
  const [prices, setPrices] = useState(null);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    api.get("/subscriber/registration-prices").then((r) => setPrices(r.data.prices || {})).catch(() => setPrices({}));
  }, []);

  const save = async () => {
    setSaving(true);
    try {
      const payload = {};
      for (const tp of REG_PRICE_TYPES) payload[tp] = Number(prices[tp] || 0);
      const r = await api.put("/subscriber/registration-prices", { prices: payload });
      setPrices(r.data.prices || {});
      toast.success("Registration prices saved");
    } catch (e) { toast.error(e?.response?.data?.detail || "Failed to save prices"); }
    finally { setSaving(false); }
  };

  return (
    <div className="am-card p-6 space-y-4 mt-6" data-testid="reg-pricing-card">
      <div className="flex items-center gap-2">
        <BadgeIndianRupee className="w-5 h-5 text-primary" />
        <h2 className="font-display text-xl font-bold">Animal Registration Pricing (₹ / animal)</h2>
      </div>
      <p className="text-xs text-muted-foreground">
        Helpers cannot change these prices — the registration fee is calculated automatically
        (quantity × price). Leave 0 to let helpers enter the fee manually. Old receipts are never recalculated.
      </p>
      {!prices ? (
        <p className="text-sm text-muted-foreground">…</p>
      ) : (
        <>
          <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-5 gap-3">
            {REG_PRICE_TYPES.map((type) => (
              <div key={type}>
                <label className="text-xs uppercase tracking-widest font-bold text-muted-foreground">{type}</label>
                <Input
                  data-testid={`reg-price-${type}`}
                  type="number" min={0} inputMode="numeric"
                  className="am-input mt-1"
                  value={prices[type] ?? ""}
                  placeholder="0"
                  onChange={(e) => setPrices({ ...prices, [type]: e.target.value })}
                />
              </div>
            ))}
          </div>
          <Button data-testid="reg-price-save" className="am-btn-primary" onClick={save} disabled={saving}>
            {saving ? "…" : "Save Pricing"}
          </Button>
        </>
      )}
    </div>
  );
}

export default function SubscriberSettings() {
  const { t } = useT();
  const [p, setP] = useState(null);
  const [saving, setSaving] = useState(false);
  const [haats, setHaats] = useState([]);

  useEffect(() => {
    api.get("/subscriber/profile").then((r) => setP(r.data));
    api.get("/subscriber/haats").then((r) => setHaats(r.data.haats || [])).catch(() => {});
  }, []);

  const save = async () => {
    setSaving(true);
    try {
      const r = await api.patch("/subscriber/profile", {
        fee_seller_per_animal: Number(p.fee_seller_per_animal),
        fee_purchaser_per_animal: Number(p.fee_purchaser_per_animal),
        fee_gatepass: Number(p.fee_gatepass),
        haat_name: p.haat_name,
        village: p.village,
        tehsil: p.tehsil,
        district: p.district,
        state: p.state,
        owner_email: p.owner_email || "",
        closing_time: p.closing_time || "18:00",
      });
      setP(r.data);
      toast.success(t("msg.saved"));
    } catch { toast.error(t("msg.saveFailed")); }
    finally { setSaving(false); }
  };

  if (!p) return <DashboardShell title="…" />;

  return (
    <DashboardShell title={t("nav.settings")} action={<HelpVideoButton screen="settings" />}>
      <div className="am-card p-6 flex items-center gap-4 mb-6" data-testid="settings-language-card">
        <span className="w-11 h-11 rounded-full bg-primary/10 text-primary grid place-items-center shrink-0">
          <Languages className="w-5 h-5" />
        </span>
        <div className="flex-1 min-w-0">
          <div className="font-display font-bold text-base leading-tight">{t("common.language")}</div>
          <div className="text-xs text-muted-foreground mt-0.5">AnimalMela · हिन्दी · English · ਪੰਜਾਬੀ · मराठी · ગુજરાતી</div>
        </div>
        <LanguageSelect />
      </div>
      <div className="grid lg:grid-cols-2 gap-6">
        <div className="am-card p-6 space-y-4">
          <h2 className="font-display text-xl font-bold">{t("sub.haatDetails")}</h2>
          <Input placeholder={t("admin.haat")} className="am-input" value={p.haat_name || ""} onChange={(e) => setP({ ...p, haat_name: e.target.value })} />
          <div className="grid grid-cols-2 gap-3">
            <Input placeholder="Village" className="am-input" value={p.village || ""} onChange={(e) => setP({ ...p, village: e.target.value })} />
            <Input placeholder="Tehsil" className="am-input" value={p.tehsil || ""} onChange={(e) => setP({ ...p, tehsil: e.target.value })} />
          </div>
          <div className="grid grid-cols-2 gap-3">
            <Input placeholder="District" className="am-input" value={p.district || ""} onChange={(e) => setP({ ...p, district: e.target.value })} />
            <Input placeholder="State" className="am-input" value={p.state || ""} onChange={(e) => setP({ ...p, state: e.target.value })} />
          </div>
          <div>
            <label className="text-xs uppercase tracking-widest font-bold text-muted-foreground">{t("sub.ownerEmail")}</label>
            <Input data-testid="settings-owner-email" type="email" placeholder="owner@example.com" className="am-input mt-1" value={p.owner_email || ""} onChange={(e) => setP({ ...p, owner_email: e.target.value })} />
          </div>
          <div>
            <label className="text-xs uppercase tracking-widest font-bold text-muted-foreground">{t("sub.closingTime")}</label>
            <Input data-testid="settings-closing-time" type="time" className="am-input mt-1" value={p.closing_time || "18:00"} onChange={(e) => setP({ ...p, closing_time: e.target.value })} />
            <p className="text-xs text-muted-foreground mt-1">{t("sub.closingTimeHint")}</p>
          </div>
        </div>
        <div className="am-card p-6 space-y-4">
          <h2 className="font-display text-xl font-bold">{t("sub.feeConfig")} (₹)</h2>
          <div>
            <label className="text-xs uppercase tracking-widest font-bold text-muted-foreground">{t("sub.feeSeller")}</label>
            <Input data-testid={TEST.subFeeSeller} type="number" className="am-input mt-1" value={p.fee_seller_per_animal ?? 0} onChange={(e) => setP({ ...p, fee_seller_per_animal: e.target.value })} />
          </div>
          <div>
            <label className="text-xs uppercase tracking-widest font-bold text-muted-foreground">{t("sub.feePurchaser")}</label>
            <Input data-testid={TEST.subFeePurchaser} type="number" className="am-input mt-1" value={p.fee_purchaser_per_animal ?? 0} onChange={(e) => setP({ ...p, fee_purchaser_per_animal: e.target.value })} />
          </div>
          <div>
            <label className="text-xs uppercase tracking-widest font-bold text-muted-foreground">{t("sub.feeGatepass")}</label>
            <Input data-testid={TEST.subFeeGatepass} type="number" className="am-input mt-1" value={p.fee_gatepass ?? 0} onChange={(e) => setP({ ...p, fee_gatepass: e.target.value })} />
          </div>
          <Button data-testid={TEST.subSaveFees} className="am-btn-primary w-full" onClick={save} disabled={saving}>{saving ? "…" : t("sub.saveSettings")}</Button>
        </div>
      </div>
      <RegistrationPricingCard />
      <DeviceSyncPanel basePath="/subscriber" />
      {haats.length > 0 && <DataManagementCard haats={haats} />}
    </DashboardShell>
  );
}
